import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Referrer {
  id: string
  user_id: string
  email: string
  referral_code: string
  total_referrals: number
  successful_referrals: number
  pending_referrals: number
  total_earned: number
  pending_payout: number
  created_at: string
}

interface Referral {
  id: string
  referrer_id: string
  referred_email: string
  status: string
  reward_amount: number
  referred_at: string
  converted_at: string | null
}

export default function ReferralPage() {
  const [referrers, setReferrers] = useState<Referrer[]>([])
  const [referrals, setReferrals] = useState<Referral[]>([])
  const [rewards, setRewards] = useState<any[]>([])
  const [payouts, setPayouts] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedReferrer, setSelectedReferrer] = useState<Referrer | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [refRes, refrlRes, rewRes, payRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/referrals/referrers'),
      fetch(API_BASE + '/referrals/'),
      fetch(API_BASE + '/referrals/rewards'),
      fetch(API_BASE + '/referrals/payouts'),
      fetch(API_BASE + '/referrals/statistics')
    ])
    if (refRes.ok) setReferrers((await refRes.json()).referrers || [])
    if (refrlRes.ok) setReferrals((await refrlRes.json()).referrals || [])
    if (rewRes.ok) setRewards((await rewRes.json()).rewards || [])
    if (payRes.ok) setPayouts((await payRes.json()).payouts || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'converted': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'expired': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Referral Program</h1>

        {statistics && (
          <div className="grid grid-cols-7 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Referrers</div>
              <div className="text-2xl font-bold">{statistics.total_referrers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Referrals</div>
              <div className="text-2xl font-bold">{statistics.total_referrals}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Successful</div>
              <div className="text-2xl font-bold text-green-600">{statistics.successful_referrals}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Conversion Rate</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.conversion_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Rewards Paid</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.total_rewards_paid)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Payouts</div>
              <div className="text-2xl font-bold text-yellow-600">{formatCurrency(statistics.pending_payouts)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Reward</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.avg_referral_value)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Top Referrers</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrals</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earned</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {referrers.map(ref => (
                    <tr key={ref.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedReferrer(ref)}>
                      <td className="px-6 py-4 text-sm">{ref.email}</td>
                      <td className="px-6 py-4 font-mono text-sm">{ref.referral_code}</td>
                      <td className="px-6 py-4">{ref.total_referrals}</td>
                      <td className="px-6 py-4 text-green-600">{ref.successful_referrals}</td>
                      <td className="px-6 py-4 font-semibold">{formatCurrency(ref.total_earned)}</td>
                      <td className="px-6 py-4 text-yellow-600">{formatCurrency(ref.pending_payout)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Referrals</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referred Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reward</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referred At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Converted</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {referrals.map(ref => (
                    <tr key={ref.id}>
                      <td className="px-6 py-4 text-sm">{ref.referred_email}</td>
                      <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(ref.status)}`}>{ref.status}</span></td>
                      <td className="px-6 py-4">{formatCurrency(ref.reward_amount)}</td>
                      <td className="px-6 py-4 text-sm">{new Date(ref.referred_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-sm">{ref.converted_at ? new Date(ref.converted_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Reward Tiers</h3>
              <div className="space-y-3">
                {rewards.map(reward => (
                  <div key={reward.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium capitalize">{reward.type.replace('_', ' ')}</span>
                      <span className="text-green-600 font-semibold">{formatCurrency(reward.amount)}</span>
                    </div>
                    <div className="text-sm text-gray-500">{reward.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {selectedReferrer && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Referrer Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span>{selectedReferrer.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Code</span>
                    <span className="font-mono">{selectedReferrer.referral_code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Member Since</span>
                    <span>{new Date(selectedReferrer.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-500">Total Referrals</span>
                      <span className="font-semibold">{selectedReferrer.total_referrals}</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-500">Successful</span>
                      <span className="font-semibold text-green-600">{selectedReferrer.successful_referrals}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pending</span>
                      <span className="font-semibold text-yellow-600">{selectedReferrer.pending_referrals}</span>
                    </div>
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-500">Total Earned</span>
                      <span className="font-semibold">{formatCurrency(selectedReferrer.total_earned)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pending Payout</span>
                      <span className="font-semibold text-yellow-600">{formatCurrency(selectedReferrer.pending_payout)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Payouts</h3>
              <div className="space-y-3">
                {payouts.slice(0, 5).map(payout => (
                  <div key={payout.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold">{formatCurrency(payout.amount)}</span>
                      <span className={`px-2 py-1 rounded text-xs ${payout.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{payout.status}</span>
                    </div>
                    <div className="text-xs text-gray-500">{payout.method} - {new Date(payout.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
